import React from 'react';

import { WidgetEditTarget } from './add-widget-modal';
import { WidgetType } from './core-widget-types';

export type WidgetFieldId =
  | 'name'
  | 'attributeSources'
  | 'customExpressionSources'
  | 'xAxisSeriesSources'
  | 'xAxisRange'
  | 'yAxisRange'
  | 'timeScale'
  | 'showIndex'
  | 'defaultStep';

export type FieldPolicy = {
  disabled: boolean;
  hidden?: boolean;
  reason?: string;
};

export type WidgetEditMode =
  | { kind: 'standalone-edit' }
  | { kind: 'dynamic-section-child-edit'; widgetType: WidgetType };

export type WidgetEditPolicy = {
  mode: WidgetEditMode;
  getFieldPolicy(field: WidgetFieldId): FieldPolicy;
};

export const DYNAMIC_SECTION_CONTROLLED_FIELD_REASON = 'Controlled by the dynamic section.';
export const UNSUPPORTED_WIDGET_FIELD_REASON = 'This setting is not available for this widget.';

const ENABLED_FIELD_POLICY: FieldPolicy = {
  disabled: false,
};

const DYNAMIC_SECTION_CONTROLLED_FIELD_POLICY: FieldPolicy = {
  disabled: true,
  reason: DYNAMIC_SECTION_CONTROLLED_FIELD_REASON,
};

const UNSUPPORTED_WIDGET_FIELD_POLICY: FieldPolicy = {
  disabled: true,
  reason: UNSUPPORTED_WIDGET_FIELD_REASON,
};

const ALL_WIDGET_EDIT_FIELDS = [
  'name',
  'attributeSources',
  'customExpressionSources',
  'xAxisSeriesSources',
  'xAxisRange',
  'yAxisRange',
  'timeScale',
  'showIndex',
  'defaultStep',
] as const satisfies readonly WidgetFieldId[];

export const CHART_WIDGET_EDIT_FIELDS = [
  'name',
  'attributeSources',
  'customExpressionSources',
  'xAxisSeriesSources',
  'xAxisRange',
  'yAxisRange',
] as const satisfies readonly WidgetFieldId[];

const ATTRIBUTE_SOURCE_WIDGET_EDIT_FIELDS = [
  'name',
  'attributeSources',
] as const satisfies readonly WidgetFieldId[];

export const SINGLE_VALUE_WIDGET_EDIT_FIELDS = ATTRIBUTE_SOURCE_WIDGET_EDIT_FIELDS;

export const SCATTER_PLOT_WIDGET_EDIT_FIELDS = ATTRIBUTE_SOURCE_WIDGET_EDIT_FIELDS;

export const HISTOGRAM_WIDGET_EDIT_FIELDS = [
  'name',
  'attributeSources',
  'xAxisRange',
  'yAxisRange',
] as const satisfies readonly WidgetFieldId[];

export const VALUE_LIST_WIDGET_EDIT_FIELDS = [
  'name',
  'attributeSources',
  'timeScale',
  'showIndex',
] as const satisfies readonly WidgetFieldId[];

export const FILE_SERIES_WIDGET_EDIT_FIELDS = [
  'name',
  'attributeSources',
  'defaultStep',
] as const satisfies readonly WidgetFieldId[];

export function createWidgetEditPolicy({
  mode,
  supportedFields,
}: {
  mode: WidgetEditMode;
  supportedFields: readonly WidgetFieldId[];
}): WidgetEditPolicy {
  const supportedFieldsSet = new Set<WidgetFieldId>(supportedFields);

  return {
    mode,
    getFieldPolicy(field) {
      if (!supportedFieldsSet.has(field)) {
        return UNSUPPORTED_WIDGET_FIELD_POLICY;
      }

      return getFieldPolicyForMode(mode, field);
    },
  };
}

function getFieldPolicyForMode(mode: WidgetEditMode, field: WidgetFieldId): FieldPolicy {
  switch (mode.kind) {
    case 'standalone-edit':
      return ENABLED_FIELD_POLICY;
    case 'dynamic-section-child-edit':
      return getDynamicSectionChildEditFieldPolicy(field);
  }
}

function getDynamicSectionChildEditFieldPolicy(field: WidgetFieldId): FieldPolicy {
  switch (field) {
    case 'attributeSources':
    case 'customExpressionSources':
      return DYNAMIC_SECTION_CONTROLLED_FIELD_POLICY;
    case 'name':
    case 'xAxisSeriesSources':
    case 'xAxisRange':
    case 'yAxisRange':
    case 'timeScale':
    case 'showIndex':
    case 'defaultStep':
      return ENABLED_FIELD_POLICY;
  }
}

export function resolveWidgetEditMode({
  editTarget,
  widgetType,
}: {
  editTarget?: WidgetEditTarget;
  widgetType: WidgetType;
}): WidgetEditMode {
  switch (editTarget?.type) {
    case 'dynamic-section':
      return {
        kind: 'dynamic-section-child-edit',
        widgetType,
      };
    default:
      return {
        kind: 'standalone-edit',
      };
  }
}

const DEFAULT_WIDGET_EDIT_POLICY = createWidgetEditPolicy({
  mode: {
    kind: 'standalone-edit',
  },
  supportedFields: ALL_WIDGET_EDIT_FIELDS,
});

export const WidgetEditPolicyContext = React.createContext<WidgetEditPolicy>(
  DEFAULT_WIDGET_EDIT_POLICY,
);

export function useWidgetFieldPolicy(field: WidgetFieldId): FieldPolicy {
  return React.useContext(WidgetEditPolicyContext).getFieldPolicy(field);
}
